
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
    import * as pdfjsLib from 'pdfjs-dist'
    import { EventBus, PDFLinkService, PDFViewer } from 'pdfjs-dist/web/pdf_viewer'
    import PdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.js'
    // Load pdf_viewer.css, stripping url() references that point to missing SVG assets
    const pdfViewerCssRaw: string = require('!!css-loader?{"url":false}!pdfjs-dist/web/pdf_viewer.css').toString()
    const pdfViewerStyle = document.createElement('style')
    pdfViewerStyle.textContent = pdfViewerCssRaw
    document.head.appendChild(pdfViewerStyle)

    pdfjsLib.GlobalWorkerOptions.workerSrc = PdfWorkerUrl

    @Component
    export default class PdfInteractiveViewer extends Vue {
        @Prop({ required: true }) readonly pdfData!: ArrayBuffer | null
        @Prop({ type: Boolean, default: false }) readonly disabled!: boolean

        private totalPages = 0
        private currentPage = 1
        private scale = 1.5
        private isFullscreen = false
        private isLoading = false

        private pdfDocument: any = null
        private pdfViewer: PDFViewer | null = null
        private eventBus: EventBus | null = null
        private linkService: PDFLinkService | null = null
        private pageAnnotations: Map<number, any[]> = new Map()
        private formValues: Record<string, string | boolean> = {}

        private readonly zoomStep = 0.25
        private readonly zoomMin = 0.5
        private readonly zoomMax = 3.0

        async mounted(): Promise<void> {
            document.addEventListener('fullscreenchange', this.onFullscreenChange)

            if (this.pdfData) {
                await this.loadPdf()
            }
        }

        beforeDestroy(): void {
            document.removeEventListener('fullscreenchange', this.onFullscreenChange)
            this.cleanup()
        }

        @Watch('pdfData')
        async onPdfDataChanged(newVal: ArrayBuffer | null): Promise<void> {
            if (newVal) {
                this.cleanup()
                await this.loadPdf()
            }
        }

        private cleanup(): void {
            this.pdfViewer?.cleanup()
            this.pdfDocument?.destroy()
            this.pdfDocument = null
            this.pdfViewer = null
            this.eventBus = null
            this.linkService = null
            this.totalPages = 0
            this.currentPage = 1
            this.pageAnnotations.clear()
            this.formValues = {}
        }

        private async loadPdf(): Promise<void> {
            if (!this.pdfData) return

            this.isLoading = true

            const container = this.$refs.viewerContainer as HTMLElement
            const viewer = this.$refs.viewer as HTMLElement
            if (!container || !viewer) return

            this.eventBus = new EventBus()
            this.linkService = new PDFLinkService({ eventBus: this.eventBus })

            this.pdfViewer = new PDFViewer({
                container,
                viewer,
                eventBus: this.eventBus,
                linkService: this.linkService,
                annotationMode: pdfjsLib.AnnotationMode.ENABLE_FORMS,
                renderInteractiveForms: true,
            } as any)

            this.linkService.setViewer(this.pdfViewer)

            this.eventBus.on('pagesinit', () => {
                this.pdfViewer!.currentScaleValue = 'page-width'
            })

            this.eventBus.on('pagechanging', (evt: any) => {
                this.currentPage = evt.pageNumber
            })

            this.eventBus.on('scalechanging', (evt: any) => {
                this.scale = evt.scale
            })

            const loadingTask = pdfjsLib.getDocument({ data: this.pdfData.slice(0) })
            this.pdfDocument = await loadingTask.promise
            this.totalPages = this.pdfDocument.numPages

            this.pdfViewer.setDocument(this.pdfDocument)
            this.linkService.setDocument(this.pdfDocument, null)

            // Pre-load annotations to detect form fields and seed default values
            for (let i = 1; i <= this.totalPages; i++) {
                const page = await this.pdfDocument.getPage(i)
                const annotations = await page.getAnnotations()
                this.pageAnnotations.set(i, annotations)

                annotations
                    .filter((a: any) => a.subtype === 'Widget' && a.fieldName && !(a.fieldName in this.formValues))
                    .forEach((a: any) => {
                        if (a.checkBox) {
                            this.formValues[a.fieldName] = a.fieldValue !== null && a.fieldValue !== 'Off'
                        } else {
                            this.formValues[a.fieldName] = a.fieldValue ?? ''
                        }
                    })
            }

            this.isLoading = false
            this.focusViewportUnlessFocusedElsewhere()
            this.$emit('loaded', this.hasFormFields())
        }

        // --- Navigation ---

        previousPage(): void {
            if (this.pdfViewer && this.currentPage > 1) {
                this.pdfViewer.currentPageNumber = this.currentPage - 1
            }
            this.focusViewportUnlessFocusedElsewhere()
        }

        nextPage(): void {
            if (this.pdfViewer && this.currentPage < this.totalPages) {
                this.pdfViewer.currentPageNumber = this.currentPage + 1
            }
            this.focusViewportUnlessFocusedElsewhere()
        }

        // --- Keyboard ---

        private onKeydown(event: KeyboardEvent): void {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
            if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return
            if (this.isTextEntryTarget(event.target)) return

            event.preventDefault()

            if (event.key === 'ArrowLeft') {
                this.previousPage()
            } else {
                this.nextPage()
            }
        }

        private isTextEntryTarget(target: EventTarget | null): boolean {
            if (!(target instanceof HTMLElement)) return false
            if (target.isContentEditable) return true

            return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
        }

        /** Focus the viewport for arrow key controls unless something else already has focus. */
        private focusViewportUnlessFocusedElsewhere(): void {
            this.$nextTick(() => {
                const active = document.activeElement
                if (active && active !== document.body) return

                this.focusViewport()
            })
        }

        private focusViewport(): void {
            this.$nextTick(() => {
                const container = this.$refs.viewerContainer as HTMLElement | undefined
                if (!container) return

                container.focus({ preventScroll: true })
            })
        }

        // --- Zoom ---

        zoomIn(): void {
            if (!this.pdfViewer || this.scale >= this.zoomMax) return
            this.pdfViewer.currentScale = Math.min(this.scale + this.zoomStep, this.zoomMax)
        }

        zoomOut(): void {
            if (!this.pdfViewer || this.scale <= this.zoomMin) return
            this.pdfViewer.currentScale = Math.max(this.scale - this.zoomStep, this.zoomMin)
        }

        fitToWidth(): void {
            if (!this.pdfViewer) return
            this.pdfViewer.currentScaleValue = 'page-width'
        }

        // --- Fullscreen ---

        async toggleFullscreen(): Promise<void> {
            const root = this.$refs.root as HTMLElement
            if (!root) return

            if (!this.isFullscreen) {
                await root.requestFullscreen()
            } else {
                await document.exitFullscreen()
            }
        }

        private onFullscreenChange = (): void => {
            this.isFullscreen = !!document.fullscreenElement
            this.focusViewport()
        }

        // --- Public API ---

        public getValues(): Record<string, string | boolean> {
            this.readFormValuesFromDom()
            return { ...this.formValues }
        }

        private readFormValuesFromDom(): void {
            const container = this.$refs.viewerContainer as HTMLElement
            if (!container) return

            this.pageAnnotations.forEach((annotations, pageNum) => {
                const pageEl = container.querySelector(`.page[data-page-number="${pageNum}"]`)
                if (!pageEl) return

                annotations
                    .filter((a) => a.subtype === 'Widget' && a.fieldName)
                    .forEach((annotation) => {
                        const section = pageEl.querySelector(`[data-annotation-id="${annotation.id}"]`)
                        if (!section) return

                        const input = section.querySelector('input, textarea, select')
                        if (!input) return

                        if (input instanceof HTMLInputElement && input.type === 'checkbox') {
                            this.formValues[annotation.fieldName] = input.checked
                        } else if (
                            input instanceof HTMLInputElement ||
                            input instanceof HTMLTextAreaElement ||
                            input instanceof HTMLSelectElement
                        ) {
                            this.formValues[annotation.fieldName] = input.value
                        }
                    })
            })
        }

        public hasFormFields(): boolean {
            let count = 0
            this.pageAnnotations.forEach((annotations) => {
                count += annotations.filter((a) => a.subtype === 'Widget' && a.fieldName).length
            })
            return count > 0
        }

        @Watch('disabled')
        onDisabledChanged(disabled: boolean): void {
            const container = this.$refs.viewerContainer as HTMLElement
            if (!container) return
            this.setInputsDisabled(container, disabled)
            this.focusViewportUnlessFocusedElsewhere()
        }

        private setInputsDisabled(container: HTMLElement, disabled: boolean): void {
            container.querySelectorAll('.annotationLayer input, .annotationLayer textarea, .annotationLayer select').forEach((el) => {
                if (disabled) {
                    el.setAttribute('disabled', 'true')
                } else {
                    el.removeAttribute('disabled')
                }
            })
        }
    }
